<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useAppStore } from '~/stores/AppStore'
import { AppTheme } from '~/types'

interface LetsTalkProps {
  textOne?: Maybe<string> | undefined
  textTwo?: Maybe<string> | undefined
  media: Maybe<FileField> | undefined
}

withDefaults(defineProps<LetsTalkProps>(), {
  textOne: 'BUILD<br class="md:hidden" />WITH<br class="md:hidden" />US',
  textTwo: 'LET’S<br class="md:hidden" />TALK'
})

const store = useAppStore()
const ctxRef = ref<HTMLElement | null>(null)
const imageRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  if (ctxRef.value && imageRef.value) {
    gsap.registerPlugin(ScrollTrigger)
    const mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      ctx = gsap.context((self) => {
        const fxImage = self.selector?.('.fx-lt-img')
        const speed = 1.2

        gsap.to(fxImage, {
          y: speed * 100,
          ease: 'none',
          scrollTrigger: {
            trigger: ctxRef.value,
            start: 'top bottom',
            end: window.innerHeight + 'px',
            invalidateOnRefresh: true,
            scrub: true
          }
        })
      }, ctxRef.value as HTMLElement)
    })
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div ref="ctxRef" class="py-10">
    <div
      class="container relative font-black leading-[0.84] tracking-tighter clamp-[4rem,90vw,6.2rem] lg:clamp-[4rem,11vw,10rem]"
    >
      <div v-html="textOne" />
      <div
        class="relative w-fit overflow-hidden"
        :class="store.theme === AppTheme.DARK ? 'bg-black' : 'bg-white'"
      >
        <Image
          v-if="media"
          ref="imageRef"
          loading="lazy"
          class="fx-lt-img absolute left-0 top-0 h-full w-auto max-w-none object-cover lg:top-1/2 lg:h-auto lg:w-full lg:-translate-y-1/2"
          sizes="auto"
          :src="media?.responsiveImage ? media?.responsiveImage?.src : media?.url"
          :srcset="media?.responsiveImage ? media?.responsiveImage?.webpSrcSet : undefined"
          :width="media?.responsiveImage ? media?.responsiveImage?.width : (media?.width ?? 1000)"
          :height="
            media?.responsiveImage ? media?.responsiveImage?.height : (media?.height ?? 1000)
          "
          :alt="media?.alt ?? ''"
        />
        <div
          class="transform-gpu lg:mt-0"
          :class="
            store.theme === AppTheme.DARK
              ? 'bg-black text-white mix-blend-multiply'
              : 'bg-white text-black mix-blend-screen'
          "
        >
          <div
            class="relative pr-5"
            :class="store.theme === AppTheme.DARK ? 'bg-black' : 'bg-white'"
            v-html="textTwo"
          />
        </div>
      </div>
      <!--
      <div
        class="fx-lt-img relative -mt-9 bg-clip-text text-transparent lg:mt-0"
        :style="{ backgroundImage: `url(${fallbackImage})` }"
        v-html="textTwo"
      />
      -->

      <ButtonGlass
        variant="arrow"
        title="Let's talk"
        aria-label="Let's talk"
        :to="'/contact'"
        size="md"
        class="!absolute -bottom-1/2 right-5 z-[1] lg:bottom-[-55%] lg:right-28 lg:hidden"
      />

      <ButtonGlass
        variant="arrow"
        title="Let's talk"
        aria-label="Let's talk"
        :to="'/contact'"
        size="lg"
        class="!absolute -bottom-1/2 right-5 z-[1] hidden lg:bottom-[-55%] lg:right-28 lg:block"
      />
    </div>
  </div>
</template>
